<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-col cols="12" align-self="center">
      <v-row>
        <v-text-field
          :label="$t('Name')"
          v-model="basket.name"
          :error-messages="nameErrors"
          @input="$v.basket.name.$touch()"
          @blur="$v.basket.name.$touch()"
        ></v-text-field>
      </v-row>
      <v-row>
        <v-select
          multiple
          v-model="basket.contentTypeIDs"
          :items="contentTypesList"
          item-text="text"
          item-value="id"
          v-bind:label="$t('ContentTypes')"
        >
          <template v-slot:prepend-item>
            <v-list-item ripple @click="toggleIncludeAllCTVersions">
              <v-list-item-action>
                <v-icon color="primary"> {{ icon }} </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title> {{ $t("AllVersions") }} </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
        </v-select>
      </v-row>
      <v-row align="center">
        <v-select
          multiple
          :label="$t('Roles')"
          v-model="basket.roleTypeIDs"
          :items="rolesList"
          item-text="text"
          item-value="id"
          :error-messages="rolesErrors"
          @input="$v.basket.roleTypeIDs.$touch()"
          @blur="$v.basket.roleTypeIDs.$touch()"
        >
          <template slot="append-outer">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  color="success"
                  icon
                  @click="onRoleButtonClick"
                >
                  <v-icon>mdi-table-plus</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("InsertData") }}</span>
            </v-tooltip>
          </template>
        </v-select>
      </v-row>
      <v-row>
        <v-text-field
          readonly
          v-model="basket.displayConditionStr"
          :label="$t('Condition')"
          :append-icon="basket.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
          @click:append="editCondition(basket)"
          :error-messages="conditionErrors"
          @input="$v.basket.conditionStr.$touch()"
          @blur="$v.basket.conditionStr.$touch()"
        ></v-text-field>
      </v-row>
      <v-row>
        <v-switch
          v-model="basket.orderAsc"
          color="green"
          :label="$t('SortResultsAscending')"
        ></v-switch>
      </v-row>
    </v-col>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="basket.contentTypeIDs"
      @on-condition-saved="onConditionChanged"
    />
    <RoleModal :ref="roleModalKey" @on-item-saved="getRoles" />
  </base-modal>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import BasketService from "@/services/BasketService";
import RoleService from "@/services/RoleService";
import RoleModal from "@/components/administration/role/RoleModal.vue";
import Basket from "@/ecm/models/Basket";
import Utils from "@/ecm/common/Utils";

export default {
  name: "FlowModal",
  components: {
    SearchCriteriaBuilderModal,
    RoleModal,
  },
  created() {
    this.getRoles();
    this.$v.$reset();
  },
  data() {
    return {
      rolesList: [],
      contentTypesList: [],
      modal: {
        title: this.$t("Basket"),
        key: "basketModal",
      },
      open: false,
      basket: new Basket(),
      conditionModalKey: "conditionModalKey",
      roleModalKey: "roleModalKey",
      includeAllCTVersions: false,
      isNew: true,
    };
  },
  mixins: [validationMixin],
  validations: {
    basket: {
      name: { required },
      roleTypeIDs: { required },
      conditionStr: { required },
    },
  },
  computed: {
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
    roleModalInstance() {
      return this.$refs[this.roleModalKey];
    },
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return (
        this.modal.title + (this.basket.name ? " - " + this.basket.name : "")
      );
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.basket.name.$dirty) return errors;
      !this.$v.basket.name.required && errors.push(this.$t("RequiredField"));
      return errors;
    },
    conditionErrors() {
      const errors = [];
      if (!this.$v.basket.conditionStr.$dirty) return errors;
      !this.$v.basket.conditionStr.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    rolesErrors() {
      const errors = [];
      if (!this.$v.basket.roleTypeIDs.$dirty) return errors;
      !this.$v.basket.roleTypeIDs.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    icon() {
      if (this.includeAllCTVersions) return "mdi-close-box";
      return "mdi-checkbox-blank-outline";
    },
  },
  methods: {
    toggleIncludeAllCTVersions() {
      this.includeAllCTVersions = !this.includeAllCTVersions;
      this.getContentTypes();
    },
    getContentTypes() {
      BasketService.getAvailableContentTypes(
        this.basket.id > 0 ? this.basket.id : null,
        this.includeAllCTVersions
      ).then((data) => {
        this.contentTypesList = data;
      });
    },
    editCondition() {
      this.conditionModalInstance.edit(this.basket.conditionStr);
    },
    onConditionChanged(condition) {
      this.basket.conditionStr = condition.conditionStr;
      this.basket.displayConditionStr = condition.displayConditionStr;
    },
    onRoleButtonClick() {
      this.roleModalInstance.new();
    },
    getRoles() {
      RoleService.getList().then((data) => {
        this.rolesList = data;
      });
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.modalInstance.progress(true);
      BasketService.save(this.basket.toDto())
        .then((result) => {
          if (this.isNew) {
            Utils.toast.success(
              this.$t("Basket") + " " + this.$t("CreatedSuccessfullyFem")
            );
          } else {
            Utils.toast.success(
              this.$t("Basket") + " " + this.$t("UpdatedSuccessfullyFem")
            );
          }
          this.hide();
          this.$emit("on-item-saved");
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    new() {
      this.basket = new Basket();
      this.includeAllCTVersions = false;
      this.getContentTypes();
      this.$v.$reset();
      this.show();
    },
    edit(dto) {
      this.basket = new Basket(dto);
      this.isNew = false;
      this.includeAllCTVersions = false;
      this.getContentTypes();
      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
      this.isNew = true;
    },
  },
};
</script>
